import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useToast
} from "@chakra-ui/react";
import { io } from "socket.io-client";
import { FaArrowLeft, FaCamera, FaCheckCircle, FaExclamationTriangle, FaEye, FaImage, FaSyncAlt, FaTrafficLight } from "react-icons/fa";
import { CameraSearchSelect } from "../common/SearchSelects";
import { toStorageImageApiSegment } from "../../utils/storageImagePath";

const getTokenHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`
});

const fetchJson = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getTokenHeaders(),
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {})
    }
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || data.message || "Erro ao comunicar com o servidor");
  return data;
};

const getImageFileName = (imagePath) => toStorageImageApiSegment(imagePath, "passages");

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};

const formatDate = (value) => {
  if (!value) return "-";
  const parts = String(value).slice(0, 10).split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return value;
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "-";
  return `R$ ${num.toFixed(2).replace(".", ",")}`;
};

const getSocketUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) return process.env.REACT_APP_SOCKET_URL;

  const { protocol, hostname, port, origin } = window.location;
  if (["3000", "3001", "3002"].includes(port)) {
    return `${protocol}//${hostname}:5000`;
  }

  return origin;
};

const BLITZ_QUERY_CONCURRENCY = 3;
const BLITZ_QUERY_DEDUP_MS = 15000;

const normalizePlate = (value) => String(value || "").trim().toUpperCase();

const toStringSafe = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
};

const flattenValues = (input, collector = []) => {
  if (input === null || input === undefined) return collector;

  if (Array.isArray(input)) {
    input.forEach((item) => flattenValues(item, collector));
    return collector;
  }

  if (typeof input === "object") {
    Object.values(input).forEach((value) => flattenValues(value, collector));
    return collector;
  }

  collector.push(String(input));
  return collector;
};

const extractVehicleInfo = (payload) => {
  const vehicleCandidates = [
    payload?.vehicle,
    payload?.veiculo,
    payload?.data?.vehicle,
    payload?.data?.veiculo,
    payload?.data,
    payload
  ].filter(Boolean);

  const pick = (keys) => {
    for (const source of vehicleCandidates) {
      for (const key of keys) {
        const value = source?.[key];
        const normalized = toStringSafe(value).trim();
        if (normalized) return normalized;
      }
    }
    return "-";
  };

  return {
    brand: pick(["brand", "make", "marca", "fabricante"]),
    model: pick(["model", "modelo"]),
    color: pick(["color", "cor"]),
    year: pick(["year", "ano_modelo", "anoModelo"]),
    renavam: pick(["renavam"]),
    chassi: pick(["chassi", "chassis"])
  };
};

const parseFinancialIssues = (payload) => {
  const issueRegex = /(pendenc|multa|debito|d[eé]bito|ipva|licenciamento|financeir|infraca|restri|bloqueio|atraso)/i;
  const negativeRegex = /(sem pend|nenhuma pend|regular|ok|quitad|n[aã]o possui)/i;

  const issues = [];

  const visit = (node, currentKey = "") => {
    if (node === null || node === undefined) return;

    if (Array.isArray(node)) {
      node.forEach((item) => visit(item, currentKey));
      return;
    }

    if (typeof node === "object") {
      Object.entries(node).forEach(([key, value]) => {
        visit(value, key);
      });
      return;
    }

    const text = String(node).trim();
    if (!text) return;

    const keyHits = issueRegex.test(currentKey || "");
    const valueHits = issueRegex.test(text);
    const valueNegates = negativeRegex.test(text);

    if ((keyHits || valueHits) && !valueNegates) {
      issues.push(text);
    }

    if ((keyHits || valueHits) && typeof node === "boolean" && node === true) {
      issues.push(currentKey || "Pendência identificada");
    }
  };

  visit(payload);

  const normalized = Array.from(new Set(issues.map((item) => item.replace(/\s+/g, " ").trim()).filter(Boolean)));
  return normalized;
};

/** Parse structured details from the API response (new format) */
const parseStructuredDetails = (payload) => {
  const detalhes = payload?.detalhes || payload?.data?.detalhes || null;
  if (!detalhes) return null;

  return {
    multas: Array.isArray(detalhes.multas) ? detalhes.multas : [],
    ipva: detalhes.ipva || null,
    licenciamento: detalhes.licenciamento || null,
    dpvat: detalhes.dpvat || null,
    restricoes: Array.isArray(detalhes.restricoes) ? detalhes.restricoes : []
  };
};

const normalizeIntegrationResult = (result) => {
  const external = result?.external_response || {};
  const mapped = external?.mapped || null;
  const rawPayload = external?.data ?? external ?? {};

  const vehicle = mapped ? {
    brand: toStringSafe(mapped.brand || mapped.marca_modelo).trim() || "-",
    model: toStringSafe(mapped.model).trim() || "-",
    color: toStringSafe(mapped.color).trim() || "-",
    year: toStringSafe(mapped.year).trim() || "-",
    renavam: toStringSafe(mapped.renavam).trim() || "-",
    chassi: toStringSafe(mapped.chassi).trim() || "-"
  } : extractVehicleInfo(rawPayload);

  const mappedIssues = Array.isArray(mapped?.restrictions)
    ? mapped.restrictions.map((item) => (
      typeof item === "object" && item !== null
        ? toStringSafe(item.mensagem || item.message || item.descricao).trim()
        : toStringSafe(item).trim()
    )).filter(Boolean)
    : mapped?.restrictions
      ? [toStringSafe(mapped.restrictions).trim()].filter(Boolean)
      : [];

  const issues = mappedIssues.length > 0 ? mappedIssues : parseFinancialIssues(rawPayload);
  const structuredDetails = parseStructuredDetails(mapped || rawPayload);
  const situacao = mapped?.situacao || rawPayload?.situacao || (issues.length > 0 ? "IRREGULAR" : "REGULAR");

  return {
    vehicle,
    issues,
    hasFinancialIssue: issues.length > 0,
    situacao,
    structuredDetails,
    rawPayload,
    mappedPayload: mapped,
    flattenedPayload: flattenValues(mapped || rawPayload).slice(0, 50)
  };
};

// ─── Status badge component ───
const StatusBadge = ({ situacao, size = "sm" }) => {
  const normalized = String(situacao || "").toUpperCase();

  if (normalized === "CONSULTANDO") {
    return (
      <Badge
        colorScheme="yellow"
        variant="subtle"
        px={2}
        py={0.5}
        borderRadius="full"
        fontSize={size === "lg" ? "sm" : "xs"}
        display="flex"
        alignItems="center"
        gap={1}
      >
        <Spinner size="xs" speed="0.8s" />
        Consultando
      </Badge>
    );
  }

  if (normalized === "ERRO") {
    return (
      <Badge
        colorScheme="gray"
        variant="subtle"
        px={2}
        py={0.5}
        borderRadius="full"
        fontSize={size === "lg" ? "sm" : "xs"}
        display="flex"
        alignItems="center"
        gap={1}
      >
        <Icon as={FaExclamationTriangle} boxSize={size === "lg" ? 4 : 3} />
        Erro
      </Badge>
    );
  }

  const isRegular = normalized === "REGULAR";
  return (
    <Badge
      colorScheme={isRegular ? "green" : "red"}
      variant="subtle"
      px={2}
      py={0.5}
      borderRadius="full"
      fontSize={size === "lg" ? "sm" : "xs"}
      display="flex"
      alignItems="center"
      gap={1}
    >
      <Icon as={isRegular ? FaCheckCircle : FaExclamationTriangle} boxSize={size === "lg" ? 4 : 3} />
      {isRegular ? "Regular" : "Irregular"}
    </Badge>
  );
};

// ─── Financial item badge ───
const FinancialStatusBadge = ({ label, status, valor }) => {
  const isPendente = String(status).toUpperCase() === "PENDENTE";
  return (
    <Box bg={isPendente ? "red.50" : "green.50"} borderWidth="1px" borderColor={isPendente ? "red.200" : "green.200"} borderRadius="md" p={3}>
      <HStack justify="space-between" align="center">
        <VStack align="start" spacing={0}>
          <Text fontSize="xs" color="gray.500" fontWeight="medium">{label}</Text>
          <Badge colorScheme={isPendente ? "red" : "green"} variant="subtle" fontSize="xs">
            {isPendente ? "PENDENTE" : "QUITADO"}
          </Badge>
        </VStack>
        {isPendente && valor > 0 && (
          <Text fontWeight="bold" color="red.600" fontSize="md">{formatCurrency(valor)}</Text>
        )}
        {!isPendente && (
          <Icon as={FaCheckCircle} color="green.400" boxSize={5} />
        )}
      </HStack>
    </Box>
  );
};

// ─── Vehicle Details Modal ───
const VehicleDetailsModal = ({ isOpen, onClose, vehicle }) => {
  if (!vehicle) return null;

  const details = vehicle.structuredDetails;
  const hasStructuredData = details !== null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxH="85vh">
        <ModalHeader pb={2}>
          <HStack spacing={3}>
            <Flex
              boxSize="40px"
              borderRadius="md"
              bg={vehicle.situacao === "REGULAR" ? "green.50" : "red.50"}
              color={vehicle.situacao === "REGULAR" ? "green.600" : "red.600"}
              align="center"
              justify="center"
            >
              <Icon as={vehicle.situacao === "REGULAR" ? FaCheckCircle : FaExclamationTriangle} boxSize={5} />
            </Flex>
            <Box>
              <HStack spacing={2} align="center">
                <Text fontSize="lg" fontWeight="bold">{vehicle.plate}</Text>
                <StatusBadge situacao={vehicle.situacao} size="lg" />
              </HStack>
              <Text fontSize="sm" color="gray.500" fontWeight="normal">
                {vehicle.cameraName} • {formatDateTime(vehicle.detectedAt)}
              </Text>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={5} align="stretch">
            {/* Vehicle Info */}
            <Box bg="gray.50" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>Dados do Veículo</Text>
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                <Box>
                  <Text fontSize="xs" color="gray.500">Marca</Text>
                  <Text fontSize="sm" fontWeight="semibold">{vehicle.brand}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Modelo</Text>
                  <Text fontSize="sm" fontWeight="semibold">{vehicle.model}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">Cor</Text>
                  <Text fontSize="sm" fontWeight="semibold">{vehicle.color}</Text>
                </Box>
                {vehicle.year && vehicle.year !== "-" && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Ano</Text>
                    <Text fontSize="sm" fontWeight="semibold">{vehicle.year}</Text>
                  </Box>
                )}
                {vehicle.renavam && vehicle.renavam !== "-" && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">RENAVAM</Text>
                    <Text fontSize="sm" fontWeight="semibold" fontFamily="mono">{vehicle.renavam}</Text>
                  </Box>
                )}
                {vehicle.chassi && vehicle.chassi !== "-" && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Chassi</Text>
                    <Text fontSize="sm" fontWeight="semibold" fontFamily="mono">{vehicle.chassi}</Text>
                  </Box>
                )}
              </SimpleGrid>
            </Box>

            {hasStructuredData ? (
              <>
                {/* Financial status cards */}
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>Situação Financeira</Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    {details.ipva && <FinancialStatusBadge label={`IPVA ${details.ipva.ano || ""}`} status={details.ipva.status} valor={details.ipva.valor} />}
                    {details.licenciamento && <FinancialStatusBadge label={`Licenciamento ${details.licenciamento.ano || ""}`} status={details.licenciamento.status} valor={details.licenciamento.valor} />}
                    {details.dpvat && <FinancialStatusBadge label={`DPVAT ${details.dpvat.ano || ""}`} status={details.dpvat.status} valor={details.dpvat.valor} />}
                  </SimpleGrid>
                </Box>

                {/* Multas table */}
                {details.multas.length > 0 && (
                  <Box>
                    <HStack mb={2}>
                      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">Multas</Text>
                      <Badge colorScheme="red" variant="solid" fontSize="xs">{details.multas.length}</Badge>
                    </HStack>
                    <TableContainer borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                      <Table size="sm" variant="simple">
                        <Thead bg="red.50">
                          <Tr>
                            <Th fontSize="xs">Descrição</Th>
                            <Th fontSize="xs" isNumeric>Valor</Th>
                            <Th fontSize="xs">Data</Th>
                            <Th fontSize="xs">Órgão</Th>
                            <Th fontSize="xs">Nº Auto</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {details.multas.map((multa, idx) => (
                            <Tr key={idx}>
                              <Td fontSize="sm">{multa.descricao}</Td>
                              <Td fontSize="sm" isNumeric fontWeight="bold" color="red.600">{formatCurrency(multa.valor)}</Td>
                              <Td fontSize="sm">{formatDate(multa.data)}</Td>
                              <Td fontSize="sm">{multa.orgao || "-"}</Td>
                              <Td fontSize="sm" fontFamily="mono">{multa.auto || "-"}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                    {details.multas.length > 0 && (
                      <HStack justify="flex-end" mt={2}>
                        <Text fontSize="xs" color="gray.500">Total em multas:</Text>
                        <Text fontSize="sm" fontWeight="bold" color="red.600">
                          {formatCurrency(details.multas.reduce((sum, m) => sum + (Number(m.valor) || 0), 0))}
                        </Text>
                      </HStack>
                    )}
                  </Box>
                )}

                {details.multas.length === 0 && (
                  <Box bg="green.50" borderWidth="1px" borderColor="green.200" borderRadius="md" p={3}>
                    <HStack>
                      <Icon as={FaCheckCircle} color="green.400" />
                      <Text fontSize="sm" color="green.700">Nenhuma multa registrada</Text>
                    </HStack>
                  </Box>
                )}

                {/* Restricoes */}
                {details.restricoes.length > 0 && (
                  <Box>
                    <HStack mb={2}>
                      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">Restrições</Text>
                      <Badge colorScheme="orange" variant="solid" fontSize="xs">{details.restricoes.length}</Badge>
                    </HStack>
                    <VStack align="stretch" spacing={2}>
                      {details.restricoes.map((restricao, idx) => (
                        <Box key={idx} bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="md" p={3}>
                          <Text fontSize="sm" fontWeight="semibold" color="orange.800">{restricao.tipo}</Text>
                          <Text fontSize="xs" color="orange.600">{restricao.descricao}</Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {details.restricoes.length === 0 && (
                  <Box bg="green.50" borderWidth="1px" borderColor="green.200" borderRadius="md" p={3}>
                    <HStack>
                      <Icon as={FaCheckCircle} color="green.400" />
                      <Text fontSize="sm" color="green.700">Nenhuma restrição encontrada</Text>
                    </HStack>
                  </Box>
                )}
              </>
            ) : (
              /* Fallback: show flat issues when no structured data */
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>Pendências Identificadas</Text>
                {vehicle.issues.length > 0 ? (
                  <VStack align="stretch" spacing={2}>
                    {vehicle.issues.map((issue, idx) => (
                      <Box key={idx} bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={3}>
                        <Text fontSize="sm" color="red.700">{issue}</Text>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Box bg="green.50" borderWidth="1px" borderColor="green.200" borderRadius="md" p={3}>
                    <HStack>
                      <Icon as={FaCheckCircle} color="green.400" />
                      <Text fontSize="sm" color="green.700">Nenhuma pendência encontrada</Text>
                    </HStack>
                  </Box>
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor="gray.100">
          <Button onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// ─── Main component ───
const BlitzInteligenteScreen = ({ selectedTypeConfig, onBackToTypes }) => {
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [blitzActive, setBlitzActive] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [imageModal, setImageModal] = useState({ open: false, alert: null, imageUrl: "", loading: false });
  const [detailsModal, setDetailsModal] = useState({ open: false, vehicle: null });
  const [integrationReady, setIntegrationReady] = useState(null); // null = não verificado, true/false
  const [checkingIntegration, setCheckingIntegration] = useState(false);
  const [integrationName, setIntegrationName] = useState("");
  const [usingMockFallback, setUsingMockFallback] = useState(false);
  const [queryQueueStats, setQueryQueueStats] = useState({ active: 0, queued: 0 });

  const socketRef = useRef(null);
  const pendingQueriesRef = useRef(new Set());
  const queuedKeysRef = useRef(new Set());
  const queryQueueRef = useRef([]);
  const activeQueriesRef = useRef(0);
  const recentQueriesRef = useRef(new Map());
  const previousImageUrl = useRef("");
  const warningShownRef = useRef(false);
  const mockFallbackRef = useRef(false);
  const toast = useToast();

  const selectedCamera = useMemo(
    () => cameras.find((camera) => String(camera.id) === String(selectedCameraId)) || null,
    [cameras, selectedCameraId]
  );

  const displayedVehicles = useMemo(
    () => (showAll ? allVehicles : alerts),
    [showAll, allVehicles, alerts]
  );

  const regularCount = useMemo(
    () => allVehicles.filter((v) => v.situacao === "REGULAR").length,
    [allVehicles]
  );

  const updateQueryQueueStats = useCallback(() => {
    setQueryQueueStats({
      active: activeQueriesRef.current,
      queued: queryQueueRef.current.length
    });
  }, []);

  const buildPassageAlertId = useCallback((passage, plate) => (
    `${passage.id || `${passage.cameraId}-${plate}-${passage.detectedAt}`}`
  ), []);

  const buildPlaceholderEntry = useCallback((passage, plate) => ({
    id: buildPassageAlertId(passage, plate),
    createdAt: new Date().toISOString(),
    plate,
    cameraId: passage.cameraId,
    cameraName: selectedCamera?.name || `Câmera ${passage.cameraId}`,
    detectedAt: passage.detectedAt,
    imagePath: passage.imagePath,
    brand: "-",
    model: "-",
    color: "-",
    year: "-",
    renavam: "-",
    chassi: "-",
    issues: [],
    situacao: "CONSULTANDO",
    queryStatus: "pending",
    structuredDetails: null,
    payloadPreview: []
  }), [buildPassageAlertId, selectedCamera?.name]);

  const upsertVehicleEntry = useCallback((vehicleEntry) => {
    setAllVehicles((current) => {
      const withoutCurrent = current.filter((item) => item.id !== vehicleEntry.id);
      return [vehicleEntry, ...withoutCurrent].slice(0, 300);
    });

    if (
      vehicleEntry.situacao !== "CONSULTANDO"
      && vehicleEntry.situacao !== "ERRO"
      && vehicleEntry.issues?.length > 0
    ) {
      setAlerts((current) => {
        const withoutCurrent = current.filter((item) => item.id !== vehicleEntry.id);
        return [vehicleEntry, ...withoutCurrent].slice(0, 150);
      });
    } else if (vehicleEntry.situacao !== "CONSULTANDO") {
      setAlerts((current) => current.filter((item) => item.id !== vehicleEntry.id));
    }
  }, []);

  const checkIntegration = useCallback(async () => {
    setCheckingIntegration(true);
    setUsingMockFallback(false);
    mockFallbackRef.current = false;
    try {
      const data = await fetchJson("/api/integrations/capabilities");
      const hasQueryVehicle = data?.capabilities?.query_vehicle === true;
      setIntegrationReady(hasQueryVehicle);

      if (hasQueryVehicle) {
        const provider = (data.providers || []).find((p) => p.capabilities?.query_vehicle);
        setIntegrationName(provider?.name || "Integração ativa");
      } else {
        setIntegrationName("");
      }
    } catch {
      // Se falhar a verificação, considera sem integração
      setIntegrationReady(false);
      setIntegrationName("");
    } finally {
      setCheckingIntegration(false);
    }
  }, []);

  const fetchCameras = useCallback(async () => {
    setLoadingCameras(true);
    try {
      const data = await fetchJson("/api/cameras");
      setCameras((data || []).sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))));
    } catch (error) {
      toast({ title: "Erro ao carregar câmeras", description: error.message, status: "error", duration: 3500, isClosable: true });
    } finally {
      setLoadingCameras(false);
    }
  }, [toast]);

  useEffect(() => {
    checkIntegration();
    fetchCameras();
  }, [checkIntegration, fetchCameras]);

  const openPassageImage = useCallback(async (alertRow) => {
    if (previousImageUrl.current) URL.revokeObjectURL(previousImageUrl.current);
    previousImageUrl.current = "";
    setImageModal({ open: true, alert: alertRow, imageUrl: "", loading: true });

    try {
      const fileName = getImageFileName(alertRow.imagePath);
      if (!fileName) throw new Error("Passagem sem imagem");

      const response = await fetch(`/api/passages/image/${fileName}`, {
        headers: getTokenHeaders()
      });
      if (!response.ok) throw new Error("Imagem da passagem não encontrada");

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      previousImageUrl.current = imageUrl;
      setImageModal({ open: true, alert: alertRow, imageUrl, loading: false });
    } catch (error) {
      setImageModal({ open: true, alert: alertRow, imageUrl: "", loading: false });
      toast({ title: "Erro ao abrir imagem", description: error.message, status: "error", duration: 3200, isClosable: true });
    }
  }, [toast]);

  const closeImageModal = useCallback(() => {
    if (previousImageUrl.current) URL.revokeObjectURL(previousImageUrl.current);
    previousImageUrl.current = "";
    setImageModal({ open: false, alert: null, imageUrl: "", loading: false });
  }, []);

  useEffect(() => {
    return () => {
      if (previousImageUrl.current) URL.revokeObjectURL(previousImageUrl.current);
    };
  }, []);

  const executeQueuedQuery = useCallback(async (job) => {
    const { passage, cacheKey, plate } = job;

    try {
      let result;
      try {
        result = await fetchJson("/api/integrations/query-vehicle", {
          method: "POST",
          body: JSON.stringify({
            plate,
            vehicle_type: passage.vehicleType
          })
        });
        if (mockFallbackRef.current) {
          setUsingMockFallback(false);
          mockFallbackRef.current = false;
        }
      } catch (realError) {
        if (String(realError.message).includes("Nenhuma integração") || String(realError.message).includes("404")) {
          if (!mockFallbackRef.current) {
            setUsingMockFallback(true);
            mockFallbackRef.current = true;
          }
          result = await fetchJson("/api/inspections/blitz/mock-query", {
            method: "POST",
            body: JSON.stringify({
              plate,
              vehicle_type: passage.vehicleType
            })
          });
        } else {
          throw realError;
        }
      }

      setProcessedCount((current) => current + 1);

      const normalized = normalizeIntegrationResult(result);
      const alertId = buildPassageAlertId(passage, plate);
      const vehicleEntry = {
        id: alertId,
        createdAt: new Date().toISOString(),
        plate,
        cameraId: passage.cameraId,
        cameraName: selectedCamera?.name || `Câmera ${passage.cameraId}`,
        detectedAt: passage.detectedAt,
        imagePath: passage.imagePath,
        brand: normalized.vehicle.brand,
        model: normalized.vehicle.model,
        color: normalized.vehicle.color,
        year: normalized.vehicle.year,
        renavam: normalized.vehicle.renavam,
        chassi: normalized.vehicle.chassi,
        issues: normalized.issues,
        situacao: normalized.situacao,
        queryStatus: "completed",
        structuredDetails: normalized.structuredDetails,
        payloadPreview: normalized.flattenedPayload
      };

      upsertVehicleEntry(vehicleEntry);
      recentQueriesRef.current.set(cacheKey, Date.now());
    } catch (error) {
      const alertId = buildPassageAlertId(passage, plate);
      upsertVehicleEntry({
        ...buildPlaceholderEntry(passage, plate),
        id: alertId,
        situacao: "ERRO",
        queryStatus: "failed",
        issues: [error.message || "Falha na consulta externa"]
      });

      if (!warningShownRef.current) {
        warningShownRef.current = true;
        toast({
          title: "Consulta externa indisponível",
          description: error.message,
          status: "warning",
          duration: 4000,
          isClosable: true
        });
      }
    } finally {
      pendingQueriesRef.current.delete(cacheKey);
      activeQueriesRef.current -= 1;
      updateQueryQueueStats();
      processQueryQueueRef.current?.();
    }
  }, [
    buildPassageAlertId,
    buildPlaceholderEntry,
    selectedCamera?.name,
    toast,
    updateQueryQueueStats,
    upsertVehicleEntry
  ]);

  const processQueryQueueRef = useRef(null);

  const processQueryQueue = useCallback(() => {
    while (
      activeQueriesRef.current < BLITZ_QUERY_CONCURRENCY
      && queryQueueRef.current.length > 0
    ) {
      const job = queryQueueRef.current.shift();
      queuedKeysRef.current.delete(job.cacheKey);
      pendingQueriesRef.current.add(job.cacheKey);
      activeQueriesRef.current += 1;
      updateQueryQueueStats();
      executeQueuedQuery(job);
    }
  }, [executeQueuedQuery, updateQueryQueueStats]);

  processQueryQueueRef.current = processQueryQueue;

  const enqueuePassageQuery = useCallback((passage) => {
    const plate = normalizePlate(passage.plateNumber);
    const cacheKey = `${passage.cameraId}:${plate}`;
    const now = Date.now();
    const recentAt = recentQueriesRef.current.get(cacheKey);

    if (!plate) return;
    if (pendingQueriesRef.current.has(cacheKey) || queuedKeysRef.current.has(cacheKey)) return;
    if (recentAt && now - recentAt < BLITZ_QUERY_DEDUP_MS) return;

    upsertVehicleEntry(buildPlaceholderEntry(passage, plate));

    queuedKeysRef.current.add(cacheKey);
    queryQueueRef.current.push({ passage, cacheKey, plate });
    updateQueryQueueStats();
    processQueryQueue();
  }, [buildPlaceholderEntry, processQueryQueue, updateQueryQueueStats, upsertVehicleEntry]);

  const handlePassageEvent = useCallback((payload) => {
    const passage = payload?.passage || payload;
    if (!passage) return;
    if (!blitzActive) return;
    if (!selectedCameraId) return;
    if (String(passage.cameraId) !== String(selectedCameraId)) return;

    enqueuePassageQuery(passage);
  }, [blitzActive, selectedCameraId, enqueuePassageQuery]);

  useEffect(() => {
    if (!blitzActive || !selectedCameraId) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const socketUrl = getSocketUrl();
    const socket = io(socketUrl, {
      path: "/socket.io/",
      transports: ["polling", "websocket"],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      timeout: 20000,
      withCredentials: true
    });

    socket.on("connect_error", () => {
      if (!warningShownRef.current) {
        warningShownRef.current = true;
        toast({
          title: "Tempo real indisponível",
          description: "Não foi possível conectar ao canal de passagens em tempo real.",
          status: "warning",
          duration: 4000,
          isClosable: true
        });
      }
    });

    socket.on("passage-created", handlePassageEvent);
    socketRef.current = socket;

    return () => {
      socket.off("passage-created", handlePassageEvent);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [blitzActive, selectedCameraId, handlePassageEvent, toast]);

  const toggleBlitz = () => {
    if (!selectedCameraId) {
      toast({ title: "Selecione uma câmera", status: "info", duration: 2500, isClosable: true });
      return;
    }

    if (!blitzActive && !integrationReady) {
      toast({
        title: "Nenhuma integração de consulta veicular configurada",
        description: "A blitz será iniciada com dados simulados. Configure uma integração real em Configurações → Integrações.",
        status: "warning",
        duration: 6000,
        isClosable: true
      });
    }

    warningShownRef.current = false;
    setBlitzActive((current) => !current);
  };

  return (
    <Box>
      <Flex justify="space-between" align={{ base: "start", lg: "center" }} gap={4} mb={5} direction={{ base: "column", lg: "row" }}>
        <HStack spacing={3} align="center">
          <Button leftIcon={<Icon as={FaArrowLeft} />} variant="outline" onClick={onBackToTypes}>
            Fiscalizações
          </Button>
          <Flex boxSize="44px" borderRadius="md" bg={`${selectedTypeConfig.colorScheme}.50`} color={`${selectedTypeConfig.colorScheme}.600`} align="center" justify="center">
            <Icon as={selectedTypeConfig.icon} boxSize={5} />
          </Flex>
          <Box>
            <Heading size="lg">{selectedTypeConfig.title}</Heading>
            <Text color="gray.600" fontSize="sm">{selectedTypeConfig.label}</Text>
          </Box>
        </HStack>
        <HStack spacing={3}>
          <Badge colorScheme={blitzActive ? "green" : "gray"} p={2} borderRadius="md">
            {blitzActive ? "Blitz ativa" : "Blitz inativa"}
          </Badge>
          <Button leftIcon={<Icon as={FaSyncAlt} />} onClick={fetchCameras} isLoading={loadingCameras} variant="outline" colorScheme="teal">
            Atualizar câmeras
          </Button>
        </HStack>
      </Flex>

      <Flex gap={5} direction={{ base: "column", xl: "row" }} align="stretch">
        <Box w={{ base: "100%", xl: "34%" }} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
          <VStack spacing={4} align="stretch">
            <HStack spacing={2}>
              <Icon as={FaTrafficLight} color="orange.500" />
              <Heading size="sm">Configuração da Blitz</Heading>
            </HStack>

            <FormControl>
              <FormLabel>Selecione a câmera</FormLabel>
              <CameraSearchSelect
                cameras={cameras}
                value={selectedCameraId}
                onChange={(event) => {
                  setSelectedCameraId(event.target.value || "");
                  if (blitzActive) setBlitzActive(false);
                }}
                placeholder="Buscar câmera por nome"
                isDisabled={loadingCameras}
              />
            </FormControl>

            <Box bg={integrationReady === false ? "red.50" : integrationReady ? "green.50" : "gray.50"} borderWidth="1px" borderColor={integrationReady === false ? "red.200" : integrationReady ? "green.200" : "gray.200"} borderRadius="md" p={3}>
              <HStack spacing={2}>
                {checkingIntegration ? (
                  <Spinner size="xs" color="gray.500" />
                ) : (
                  <Badge colorScheme={integrationReady ? "green" : integrationReady === null ? "gray" : "red"}>
                    {integrationReady ? "Integração ativa" : integrationReady === null ? "Verificando..." : "Sem integração"}
                  </Badge>
                )}
                {!checkingIntegration && !integrationReady && (
                  <Button size="xs" variant="link" colorScheme="teal" onClick={checkIntegration}>
                    Verificar novamente
                  </Button>
                )}
              </HStack>
              {integrationReady && integrationName && (
                <Text fontSize="xs" color="green.700" mt={1} fontWeight="medium">
                  {integrationName}
                </Text>
              )}
              {integrationReady === false && (
                <Text fontSize="xs" color="red.600" mt={1}>
                  Nenhuma integração com consulta de veículos está configurada. Acesse Configurações → Integrações para cadastrar.
                </Text>
              )}
            </Box>

            {usingMockFallback && blitzActive && (
              <Box bg="yellow.50" borderWidth="1px" borderColor="yellow.300" borderRadius="md" p={2}>
                <Text fontSize="xs" color="yellow.800" fontWeight="medium">
                  ⚠ Usando dados simulados (mock). Configure uma integração real em Configurações → Integrações para consultar dados reais.
                </Text>
              </Box>
            )}

            <Button
              colorScheme={blitzActive ? "red" : "teal"}
              onClick={toggleBlitz}
              leftIcon={<Icon as={blitzActive ? FaTrafficLight : FaCamera} />}
              isDisabled={!selectedCameraId || checkingIntegration}
            >
              {blitzActive ? "Encerrar Blitz" : "Iniciar Blitz"}
            </Button>

            <Box bg="gray.50" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
              <Text fontSize="sm" color="gray.700">
                Câmera ativa: <strong>{selectedCamera?.name || "Nenhuma"}</strong>
              </Text>
              <Text fontSize="sm" color="gray.700">
                Passagens consultadas: <strong>{processedCount}</strong>
              </Text>
              <Divider my={1} />
              <Text fontSize="sm" color="red.600">
                <Icon as={FaExclamationTriangle} mr={1} boxSize={3} />
                Com pendências: <strong>{alerts.length}</strong>
              </Text>
              <Text fontSize="sm" color="green.600">
                <Icon as={FaCheckCircle} mr={1} boxSize={3} />
                Regulares: <strong>{regularCount}</strong>
              </Text>
              {(queryQueueStats.active > 0 || queryQueueStats.queued > 0) && (
                <>
                  <Divider my={1} />
                  <Text fontSize="sm" color="orange.600">
                    <Spinner size="xs" speed="0.8s" mr={2} />
                    Consultas em andamento: <strong>{queryQueueStats.active}</strong>
                    {queryQueueStats.queued > 0 && (
                      <> · na fila: <strong>{queryQueueStats.queued}</strong></>
                    )}
                  </Text>
                </>
              )}
            </Box>

            <Text fontSize="sm" color="gray.600">
              Cada passagem aparece na lista imediatamente. As consultas externas são processadas em fila, sem bloquear novas detecções.
            </Text>
          </VStack>
        </Box>

        <Box flex="1" bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
          <Flex align="center" justify="space-between" p={4} borderBottomWidth="1px" borderColor="gray.200">
            <HStack spacing={2}>
              <Icon as={FaTrafficLight} color="orange.500" />
              <Heading size="sm">{showAll ? "Todos os Veículos" : "Alertas da Blitz"}</Heading>
              <Badge colorScheme={showAll ? "blue" : "orange"}>{displayedVehicles.length}</Badge>
            </HStack>
            <HStack spacing={3}>
              <HStack spacing={2}>
                <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">Mostrar todos</Text>
                <Switch
                  size="sm"
                  colorScheme="teal"
                  isChecked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                />
              </HStack>
            </HStack>
          </Flex>

          {displayedVehicles.length === 0 ? (
            !blitzActive ? (
              <Flex py={16} direction="column" align="center" justify="center" color="gray.600">
                <Icon as={FaTrafficLight} boxSize={7} color="gray.400" />
                <Text mt={3}>Inicie a blitz para monitorar passagens em tempo real</Text>
              </Flex>
            ) : (
              <Flex py={16} direction="column" align="center" justify="center" color="gray.600">
                <Spinner size="lg" color="orange.400" mb={3} />
                <Text>{showAll ? "Aguardando passagens de veículos..." : "Aguardando passagens com pendências financeiras ou multas..."}</Text>
              </Flex>
            )
          ) : (
            <TableContainer maxH="680px" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th>Status</Th>
                    <Th>Placa</Th>
                    <Th>Marca/Modelo/Cor</Th>
                    <Th>Pendências</Th>
                    <Th>Horário</Th>
                    <Th textAlign="center">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {displayedVehicles.map((row) => (
                    <Tr
                      key={row.id}
                      bg={row.situacao === "REGULAR" ? "green.50" : undefined}
                      _hover={{ bg: row.situacao === "REGULAR" ? "green.100" : "gray.50" }}
                      cursor="pointer"
                      onClick={() => setDetailsModal({ open: true, vehicle: row })}
                    >
                      <Td>
                        <StatusBadge situacao={row.situacao} />
                      </Td>
                      <Td>
                        <Text fontWeight="bold">{row.plate}</Text>
                        <Text fontSize="xs" color="gray.500">{row.cameraName}</Text>
                      </Td>
                      <Td>
                        <Text>{row.brand}</Text>
                        <Text>{row.model}</Text>
                        <Text color="gray.600">{row.color}</Text>
                      </Td>
                      <Td>
                        {row.situacao === "CONSULTANDO" ? (
                          <HStack spacing={2}>
                            <Spinner size="xs" color="orange.400" />
                            <Text fontSize="xs" color="gray.500">Aguardando integração...</Text>
                          </HStack>
                        ) : row.issues.length > 0 ? (
                          <VStack align="start" spacing={1}>
                            {row.issues.slice(0, 3).map((issue) => (
                              <Badge key={`${row.id}-${issue}`} colorScheme="red" variant="subtle" fontSize="xs" maxW="250px" isTruncated>{issue}</Badge>
                            ))}
                            {row.issues.length > 3 && (
                              <Text fontSize="xs" color="gray.500">+{row.issues.length - 3} itens</Text>
                            )}
                          </VStack>
                        ) : (
                          <Badge colorScheme="green" variant="subtle">Sem pendências</Badge>
                        )}
                      </Td>
                      <Td>{formatDateTime(row.detectedAt)}</Td>
                      <Td textAlign="center">
                        <HStack spacing={1} justify="center">
                          <Tooltip label="Ver detalhes">
                            <IconButton
                              aria-label="Ver detalhes do veículo"
                              icon={<Icon as={FaEye} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetailsModal({ open: true, vehicle: row });
                              }}
                            />
                          </Tooltip>
                          <Tooltip label="Abrir foto da passagem">
                            <IconButton
                              aria-label="Abrir foto da passagem"
                              icon={<Icon as={FaImage} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="teal"
                              onClick={(e) => {
                                e.stopPropagation();
                                openPassageImage(row);
                              }}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Flex>

      {/* Image Modal */}
      <Modal isOpen={imageModal.open} onClose={closeImageModal} size="4xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaImage} color="teal.500" />
              <Box>
                <Text>Passagem {imageModal.alert?.plate}</Text>
                <Text fontSize="sm" color="gray.600" fontWeight="normal">{formatDateTime(imageModal.alert?.detectedAt)}</Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {imageModal.loading ? (
              <Flex h="420px" align="center" justify="center"><Spinner size="xl" color="teal.500" thickness="4px" /></Flex>
            ) : imageModal.imageUrl ? (
              <Image src={imageModal.imageUrl} alt="Imagem da passagem" maxH="70vh" mx="auto" borderRadius="md" />
            ) : (
              <Flex h="220px" align="center" justify="center" direction="column" color="gray.600">
                <Icon as={FaImage} color="gray.400" boxSize={7} />
                <Text mt={3}>Imagem indisponível</Text>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeImageModal}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Vehicle Details Modal */}
      <VehicleDetailsModal
        isOpen={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, vehicle: null })}
        vehicle={detailsModal.vehicle}
      />
    </Box>
  );
};

export default BlitzInteligenteScreen;
